import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { createPageUrl } from "../utils";
import { ArrowLeft } from "lucide-react";
import Button from "../components/ui/Button";
import Footer from "../components/home/Footer";

import suspension from "../img/products/electrical/suspension.webp";
import aparatus from "../img/products/electrical/aparatus.webp";
import capandpost from "../img/products/electrical/cap & post.png";
import cap from "../img/products/electrical/cap.png";
import pin from "../img/products/electrical/pin.jpg";
import polymer from "../img/products/electrical/polymer.jpg";
import silicon from "../img/products/electrical/silicon.jpg";
import traction from "../img/products/electrical/traction-insulator-803204924-oas2tl5i.jpeg";
import solid from "../img/products/electrical/solid.jpg";

/* -----------------------------------
   PRODUCT DATA – PDF ALIGNED (9 ITEMS)
----------------------------------- */

const products = [
  {
    name: "Solid Core Station Post Insulators",
    category: "Porcelain Insulators",
    image: solid,
    rating: "Up to 1200 kV",
    creepage: "25 mm/kV & 31 mm/kV",
  },
  {
    name: "Traction Insulators for Railway Electrification",
    category: "Porcelain Insulators",
    image: traction,
    rating: "Up to 25 kV",
    creepage: "25 mm/kV & 31 mm/kV",
  },
  {
    name: "Apparatus Porcelain Insulators",
    category: "Porcelain Insulators",
    image: aparatus,
    rating: "Up to 800 kV",
  },
  {
    name: "Silicone (RTV) Coated Insulators",
    category: "Porcelain Insulators",
    image: silicon,
    rating: "Up to 25 kV",
  },
  {
    name: "Suspension Disc Insulators",
    category: "Porcelain Insulators",
    image: suspension,
    rating: "Up to 25 kV",
    creepage: "16 – 40 mm/kV",
    profile:
      "Normal, Semi Fog, Anti Fog, Super Fog & Open Profile",
  },
  {
    name: "Pin Insulators",
    category: "Porcelain Insulators",
    image: pin,
    rating: "Up to 69 kV",
    strength: "Up to 10 kN",
    creepage: "Up to 1080 mm/kV",
  },
  {
    name: "Cap & Pedestal Post Insulators",
    category: "Porcelain Insulators",
    image: cap,
    rating: "Up to 36 kV",
    strength: "Up to 31 kN",
    creepage: "Up to 25 mm/kV",
  },
  {
    name: "Polymer Insulators",
    category: "Polymer & Suspension Insulators",
    image: polymer,
    rating: "11 kV, 22 kV & 33 kV",
    strength: "10 kN, 45 kN & 70 kN",
    creepage: "25 mm/kV & 31 mm/kV",
  },
  {
    name: "Polymer Suspension Insulators",
    category: "Polymer & Suspension Insulators",
    image: capandpost,
    rating: "66 kV – 400 kV",
    strength: "10 kN, 45 kN & 70 kN",
    creepage: "25 mm/kV & 31 mm/kV",
  },
];

/* -----------------------------------
   COMPONENT
----------------------------------- */

const InsulatorsProducts = () => {
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">

      {/* HEADER */}
      <div className="bg-gradient-to-br from-[#1a237e] via-[#303f9f] to-[#1a237e] text-white py-20">
        <div className="max-w-7xl mx-auto px-6">

          <Link to={createPageUrl("Home")}>
            <Button variant="ghost" className="text-white hover:text-[#f9d71c] mb-8 -ml-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>

          <h1 className="text-4xl md:text-6xl font-light mb-4">
            Electrical <span className="font-semibold">Insulators</span>
          </h1>

          <p className="text-white/90 text-lg max-w-3xl">
            Porcelain and polymer insulators designed for power transmission,
            substations and railway electrification.
          </p>

        </div>
      </div>

      {/* PRODUCTS GRID */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">

          {products.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden"
            >
              {/* IMAGE */}
              <div className="h-64 bg-white flex items-center justify-center overflow-hidden">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-contain p-4
                             transition-transform duration-300
                             group-hover:scale-105"
                />
              </div>

              {/* CONTENT */}
              <div className="p-6">
                <span className="text-[#303f9f] text-xs uppercase font-medium">
                  {p.category}
                </span>

                <h3 className="text-xl font-semibold text-[#1a237e] mt-2 mb-4">
                  {p.name}
                </h3>

                <div className="space-y-2 text-sm text-gray-600">
                  {p.rating && <p><strong>Rating:</strong> {p.rating}</p>}
                  {p.strength && <p><strong>Mechanical Strength:</strong> {p.strength}</p>}
                  {p.creepage && <p><strong>Creepage Distance:</strong> {p.creepage}</p>}
                  {p.profile && <p><strong>Profile:</strong> {p.profile}</p>}
                </div>
              </div>

            </motion.div>
          ))}

        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-16 bg-[#1a237e] rounded-3xl p-8 md:p-12 text-center"
        >
          <h3 className="text-2xl md:text-3xl font-light text-white mb-4">
            Need Custom <span className="font-medium text-[#f9d71c]">Specifications?</span>
          </h3>

          <p className="text-white/70 max-w-2xl mx-auto mb-6">
            Manufactured as per IEC & IS standards with custom ratings available.
          </p>

          <Link to="/#contact">
            <Button className="bg-[#f9d71c] text-[#1a237e] hover:bg-[#f9d71c]/90 px-8 py-6 text-lg font-medium rounded-full">
              Request Quote
            </Button>
          </Link>
        </motion.div>

      </div>

      <Footer />
    </div>
  );
};

export default InsulatorsProducts;
